import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as ChevronUpIcon } from "../../../assets/svg/chevron-up.svg";
import { ReactComponent as ChevronDownIcon } from "../../../assets/svg/chevron-down.svg";

export interface IDropdownOptionProp {
	label: string;
	value: string;
}

interface IDropdownBoxProps {
	defaultText: string;
	options: IDropdownOptionProp[];
	onSelected: (selectedOption: IDropdownOptionProp) => void;
	disabled?: boolean;
}

const DropdownBox: React.FC<IDropdownBoxProps> = ({
	defaultText,
	options,
	onSelected,
	disabled = false,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [openAbove, setOpenAbove] = useState(false); // State to track dropdown position
	const dropdownRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [selectedValue, setSelectedValue] = useState(defaultText);
	const handleToggle = () => setIsOpen(!isOpen);

	const handleClickOutside = (event: MouseEvent) => {
		if (
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target as Node)
		) {
			setIsOpen(false);
		}
	};

	const adjustDropdownPosition = () => {
		if (buttonRef.current && dropdownRef.current) {
			const buttonRect = buttonRef.current.getBoundingClientRect();
			const dropdownHeight = dropdownRef.current.scrollHeight;
			const viewportHeight = window.innerHeight;

			// Check if there's enough space below the button
			const spaceBelow = viewportHeight - buttonRect.bottom;
			setOpenAbove(spaceBelow < dropdownHeight);
		}
	};

	useEffect(() => {
		if (isOpen) {
			adjustDropdownPosition();
		}
	}, [isOpen]);

	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () =>
			document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	return (
		<div
			className="bg-slate-50 relative inline-block text-left w-full"
			ref={dropdownRef}
		>
			<button
				id="dropdownDefaultButton"
				onClick={handleToggle}
				className={`font-Poppins tracking-wider border-b-2 hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium text-md p-2 py-1 text-center inline-flex items-center w-full`}
				type="button"
				ref={buttonRef}
				disabled={disabled}
			>
				<div className="flex items-center justify-between w-full gap-2">
					<span
						className={`font-Poppins font-medium text-md w-full text-left text-nowrap ${disabled ? "text-gray-300" : "text-black"}`}
					>
						{selectedValue}
					</span>
					{isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
				</div>
			</button>

			{isOpen && (
				<div
					id="dropdown"
					className={`z-100 max-h-23 absolute left-0 bg-white divide-y divide-gray-100 shadow w-full ${
						openAbove ? "bottom-full mb-1" : "top-full mt-1"
					}`}
				>
					<ul
						className="py-2 text-md text-gray-700 max-h-36 overflow-y-auto w-full"
						aria-labelledby="dropdownDefaultButton"
					>
						{options.map((option, index) => (
							<li
								className="hover:bg-gray-200 font-Poppins font-medium text-md ps-2 py-2 text-nowrap w-full"
								onClick={() => {
									setSelectedValue(option.label);
									onSelected(option);
									setIsOpen(false);
								}}
								key={index}
							>
								{option.label}
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};

export default DropdownBox;
